import { useEffect, useRef, useMemo, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'

import {
  getAttendanceIntelligence,
  getOrgEventsForReport,
} from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import { EventSelector } from '../EventSelector'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { ReportRefreshButton } from '../ReportRefreshButton'
import AttendanceIntelligenceArrivalChart from './AttendanceIntelligenceArrivalChart'
import AttendanceIntelligenceHeatmapMap from './AttendanceIntelligenceHeatmapMap'
import AttendanceIntelligenceInsightCards from './AttendanceIntelligenceInsightCards'
import AttendanceIntelligenceSegmentBreakdown from './AttendanceIntelligenceSegmentBreakdown'
import AttendanceIntelligenceSummary from './AttendanceIntelligenceSummary'

import './attendanceIntelligenceWrapper.scss'

export default function AttendanceIntelligenceWrapper({ organization }) {
  const [events, setEvents] = useState([])
  const [eventUUID, setEventUUID] = useState('')
  const [reportData, setReportData] = useState(null)
  const [isLoadingEvents, setIsLoadingEvents] = useState(true)
  const [isLoadingReport, setIsLoadingReport] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingReport)
  const [loadError, setLoadError] = useState(null)
  const [showTutorial, setShowTutorial] = useState(false)
  const tutorialVideoRef = useRef(null)
  const organizationUUID = organization?.uuid

  const TUTORIAL_VIDEO_SOURCE =
    'https://blocktickets.nyc3.cdn.digitaloceanspaces.com/tutorials/Attendance%20Intelligence_1080p.mp4'

  const handleCloseTutorial = () => {
    if (tutorialVideoRef.current) {
      tutorialVideoRef.current.pause()
      tutorialVideoRef.current.currentTime = 0
    }
    setShowTutorial(false)
  }

  useEffect(() => {
    if (!organizationUUID) return

    let cancelled = false
    setIsLoadingEvents(true)

    getOrgEventsForReport(organizationUUID)
      .then((res) => {
        if (cancelled) return
        const list = Array.isArray(res?.data) ? res.data : []
        setEvents(list)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load events', err)
        setLoadError('Could not load events for this organization.')
      })
      .finally(() => {
        if (!cancelled) setIsLoadingEvents(false)
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID])

  useEffect(() => {
    if (!organizationUUID || !eventUUID) {
      setReportData(null)
      return
    }

    let cancelled = false
    setIsLoadingReport(true)
    setLoadError(null)

    getAttendanceIntelligence(organizationUUID, eventUUID)
      .then((res) => {
        if (cancelled) return
        setReportData(res?.data ?? null)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load attendance intelligence', err)
        setLoadError('Could not load attendance intelligence for this event.')
        setReportData(null)
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingReport(false)
          completeRefresh()
        }
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID, eventUUID, refreshToken])

  const selectedEvent = useMemo(
    () => events.find((event) => event.uuid === eventUUID) || null,
    [events, eventUUID]
  )

  const isLoading = isLoadingEvents || isLoadingReport
  const hasData = reportData && reportData.error !== 'event_not_found'

  return (
    <section
      className="spacer-sm attendance-intelligence-wrapper"
      id="attendance-intelligence-wrapper"
    >
      <header className="section-header-sm report-page-header">
        <div className="section-heading">
          <h1>Attendance Intelligence</h1>
        </div>
        <p className="section-header-desc">
          Analyze scan timing, section turnout, buyer segments, and no-show
          impact for {organization?.name || 'your organization'}.
        </p>
        <p className="section-header-desc mt-2">
          <strong>Note: </strong>
          <Button
            variant="link"
            className="p-0 align-baseline"
            onClick={() => setShowTutorial(true)}
          >
            View Tutorial
          </Button>
        </p>
      </header>

      <Modal
        show={showTutorial}
        onHide={handleCloseTutorial}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Attendance Intelligence Tutorial</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <video
            ref={tutorialVideoRef}
            controls
            autoPlay
            style={{ width: '100%' }}
          >
            <source src={TUTORIAL_VIDEO_SOURCE} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Modal.Body>
      </Modal>

      <Card
        body
        className="card--xs card-with-border attendance-intelligence-controls"
      >
        <Stack
          direction="horizontal"
          gap={3}
          className="align-items-end flex-wrap"
        >
          <EventSelector
            events={events}
            selected={eventUUID}
            setSelected={setEventUUID}
            isLoading={isLoadingEvents}
            groupByYear
          />
          <ReportRefreshButton
            onRefresh={refresh}
            isRefreshing={isLoadingReport}
            disabled={!eventUUID || isLoadingReport}
          />
        </Stack>
        {selectedEvent && (
          <p className="text-muted small mb-0 mt-2">
            Showing attendance data for <strong>{selectedEvent.name}</strong>
            {reportData?.snapshotDate && (
              <>
                {' '}
                · snapshot{' '}
                {new Date(reportData.snapshotDate).toLocaleDateString(
                  undefined,
                  {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  }
                )}
              </>
            )}
          </p>
        )}
      </Card>

      {loadError && (
        <Card body className="card--xs card-with-border mt-3">
          <p className="text-danger mb-0">{loadError}</p>
        </Card>
      )}

      {isLoading && eventUUID && <PageLoadingContainer />}

      {!isLoading && eventUUID && reportData?.error === 'event_not_found' && (
        <Card body className="card--xs card-with-border mt-3">
          <p className="text-muted mb-0">
            Event not found for this organization.
          </p>
        </Card>
      )}

      {!isLoading && hasData && (
        <>
          <div className="mt-3">
            <AttendanceIntelligenceSummary kpis={reportData.kpis} />
          </div>

          <Card
            body
            className="card--xs card-with-border mt-3 attendance-intelligence-chart-card"
          >
            <Stack
              direction="horizontal"
              className="justify-content-between align-items-center flex-wrap gap-2 mb-3"
            >
              <h2 className="h5 mb-0">Arrival curve</h2>
              <span className="text-muted small">
                Scans by 15-minute bucket
              </span>
            </Stack>
            <AttendanceIntelligenceArrivalChart
              arrivalCurve={reportData.arrivalCurve}
            />
          </Card>

          <div className="mt-3">
            <h2 className="h5 mb-3">Insights</h2>
            <AttendanceIntelligenceInsightCards
              insightCards={reportData.insightCards}
            />
          </div>

          <Card
            body
            className="card--xs card-with-border mt-3 attendance-intelligence-heatmap-card"
          >
            <Stack
              direction="horizontal"
              className="justify-content-between align-items-center flex-wrap gap-2 mb-3"
            >
              <h2 className="h5 mb-0">Seatmap heatmap</h2>
              <span className="text-muted small">Colored by scan rate</span>
            </Stack>
            <AttendanceIntelligenceHeatmapMap heatmap={reportData.heatmap} />
          </Card>

          <Card
            body
            className="card--xs card-with-border mt-3 attendance-intelligence-segment-card"
          >
            <Stack
              direction="horizontal"
              className="justify-content-between align-items-center flex-wrap gap-2 mb-3"
            >
              <h2 className="h5 mb-0">Segment breakdown</h2>
              <span className="text-muted small">
                Season, groups, first-time, box office, online
              </span>
            </Stack>
            <AttendanceIntelligenceSegmentBreakdown
              segmentBreakdown={reportData.segmentBreakdown}
            />
          </Card>
        </>
      )}

      {!eventUUID && !isLoadingEvents && (
        <Card body className="card--xs card-with-border mt-3">
          <p className="text-muted mb-0">
            Select an event to view attendance intelligence.
          </p>
        </Card>
      )}
    </section>
  )
}
